import { lazy } from 'react';
import type { AnyObject, ScreenItem } from 'shared/src/types';
import { Screens } from './pages/Screens/Screens.page';
import { FragmentScreens } from './pages/FragmentScreens/FragmentScreens.page';
import { WebViewScreen } from './pages/WebView';
import { OrderListScreen } from './pages/OrderList';
import { OrderDetailScreen } from './pages/OrderDetail';
import { EventDetailScreen } from './pages/EventDetail';
import {
  AffiliateEventDetailScreen,
  BenefitsScreen,
  StyleScreen,
  CommunityTalkHomeScreen,
  FunTalkScreen,
  ChatBotScreen,
  FortuneHomeScreen,
  LifeFortuneDailyScreen,
  ContentStyleHomeScreen,
  NovelHomeScreen,
  CouponsScreen,
  EventListScreen,
  EventSegmentDetailScreen,
  HelpInquiryScreen,
  HelpScreen,
  MarketInfoScreen,
  MembershipScreen,
  MumulLiveProfileSnoScreen,
  MumulLiveProfilesScreen,
  NotificationsScreen,
  OrderCancellationsScreen,
  OrderStatusScreen,
  OrderDeliveryPostScreen,
  OrderDeliveryPostOrderItemScreen,
  OrderExchangeScreen,
  OrderExchangeSellersScreen,
  OrderReturnScreen,
  OrderReturnDetailScreen,
  OrderSalesInvoiceScreen,
  PaymentCheckoutScreen,
  PaymentSuccessScreen,
  PaymentFailScreen,
  PlaceGoodsDetailScreen,
  TodayOnboardingScreen,
  PlaceCategoryListScreen,
  PlaceMapScreen,
  PlaceSearchScreen,
  PlaceReservationListScreen,
  PointsScreen,
  TodayDeliveryScreen,
} from './pages/WebviewScreens';
import { WEB_ROUTE_PATHS } from 'shared/src/webRoutePaths';
import { OrderCancellationsDetailScreen } from './pages/WebviewScreens/WebviewScreens';

type CommonScreenParams<T> = Partial<{
  instanceId: string;
  next_token: string;
  screen_name: string;
}> &
  T;

export type ScreensPageParamList = {
  BrandHome: CommonScreenParams<AnyObject>;
  WebViewScreen: CommonScreenParams<{ url: string; title?: string }>;
};

const devOnlyScreens: ScreenItem[] = __DEV__
  ? [
      {
        name: 'ScreenRenderer',
        component: lazy(() =>
          import('./pages/ScreenRenderer').then(m => ({
            default: m.ScreenRenderer,
          })),
        ),
        path: '/screen-renderer/:type',
        options: {
          title: 'Screen Renderer',
        },
      },
    ]
  : [];

export const ScreensPageScreens: ScreenItem[] = [
  {
    name: 'Screens',
    component: Screens,
    path: WEB_ROUTE_PATHS.Screens,
    options: {
      title: '에이블리 스토어 - 에이블리',
    },
    convertParams: props => {
      const formattedExtras =
        typeof props.extras === 'string'
          ? (() => {
              try {
                return JSON.parse(props.extras as string);
              } catch {
                return {};
              }
            })()
          : props.extras;

      return {
        instanceId: props.instanceId,
        ...formattedExtras,
      };
    },
  },
  {
    name: 'FragmentScreens',
    component: FragmentScreens,
    path: WEB_ROUTE_PATHS.FragmentScreens,
    convertParams: props => {
      const formattedExtras =
        typeof props.extras === 'string'
          ? (() => {
              try {
                return JSON.parse(props.extras as string);
              } catch {
                return {};
              }
            })()
          : props.extras;

      return {
        instanceId: props.instanceId,
        ...formattedExtras,
      };
    },
  },
  {
    name: 'WebViewScreen',
    component: WebViewScreen,
    path: '/webview',
  },
  // 내용은 웹뷰지만 주소를 유지해야 해서 전용 화면으로 둔 것들.
  // (범용 WebViewScreen 으로 열면 목적지가 ?url=... 로 들어가 주소가 유지되지 않는다)
  {
    name: 'OrderList',
    component: OrderListScreen,
    path: WEB_ROUTE_PATHS.OrderList,
    options: {
      title: '주문 · 예약 내역 - 에이블리 스토어',
    },
  },
  {
    name: 'OrderDetail',
    component: OrderDetailScreen,
    path: WEB_ROUTE_PATHS.OrderDetail,
    options: {
      title: '주문 상세 - 에이블리 스토어',
    },
  },
  {
    name: 'EventList',
    component: EventListScreen,
    path: WEB_ROUTE_PATHS.EventList,
    options: {
      title: '쿠폰 / 이벤트 - 에이블리 스토어',
    },
  },
  {
    name: 'EventDetail',
    component: EventDetailScreen,
    path: WEB_ROUTE_PATHS.EventDetail,
  },
  {
    name: 'EventSegmentDetail',
    component: EventSegmentDetailScreen,
    path: WEB_ROUTE_PATHS.EventSegmentDetail,
    options: {
      title: '기획전 - 에이블리 스토어',
    },
  },
  {
    name: 'AffiliateEventDetail',
    component: AffiliateEventDetailScreen,
    path: WEB_ROUTE_PATHS.AffiliateEventDetail,
    options: {
      title: '기획전 - 에이블리 스토어',
    },
  },
  {
    name: 'PaymentCheckout',
    component: PaymentCheckoutScreen,
    path: WEB_ROUTE_PATHS.PaymentCheckout,
    options: {
      title: '결제 - 에이블리 스토어',
    },
  },
  {
    name: 'PaymentSuccess',
    component: PaymentSuccessScreen,
    path: WEB_ROUTE_PATHS.PaymentSuccess,
    options: {
      title: '주문 완료 - 에이블리 스토어',
    },
  },
  {
    name: 'PaymentFail',
    component: PaymentFailScreen,
    path: WEB_ROUTE_PATHS.PaymentFail,
    options: {
      title: '결제 실패 - 에이블리 스토어',
    },
  },
  {
    name: 'PlaceGoodsDetail',
    component: PlaceGoodsDetailScreen,
    path: WEB_ROUTE_PATHS.PlaceGoodsDetail,
    options: {
      title: '상품 상세 - 에이블리 스토어',
    },
  },
  {
    name: 'TodayOnboarding',
    component: TodayOnboardingScreen,
    path: WEB_ROUTE_PATHS.TodayOnboarding,
    options: {
      title: '에이블리 스토어',
    },
  },
  {
    name: 'PlaceCategoryList',
    component: PlaceCategoryListScreen,
    path: WEB_ROUTE_PATHS.PlaceCategoryList,
    options: {
      title: '플레이스 - 에이블리 스토어',
    },
  },
  {
    name: 'PlaceMap',
    component: PlaceMapScreen,
    path: WEB_ROUTE_PATHS.PlaceMap,
    options: {
      title: '플레이스 지도 - 에이블리 스토어',
    },
  },
  {
    name: 'PlaceSearch',
    component: PlaceSearchScreen,
    path: WEB_ROUTE_PATHS.PlaceSearch,
    options: {
      title: '검색 - 에이블리 스토어',
    },
  },
  {
    name: 'PlaceReservationList',
    component: PlaceReservationListScreen,
    path: WEB_ROUTE_PATHS.PlaceReservationList,
    options: {
      title: '예약 내역 - 에이블리 스토어',
    },
  },
  {
    name: 'MarketInfo',
    component: MarketInfoScreen,
    path: WEB_ROUTE_PATHS.MarketInfo,
    options: {
      title: '마켓 정보 - 에이블리 스토어',
    },
  },
  {
    name: 'MumulLiveProfiles',
    component: MumulLiveProfilesScreen,
    path: WEB_ROUTE_PATHS.MumulLiveProfiles,
    options: {
      title: 'LIVE 채널 - 에이블리 스토어',
    },
  },
  {
    name: 'MumulLiveProfileSno',
    component: MumulLiveProfileSnoScreen,
    path: WEB_ROUTE_PATHS.MumulLiveProfileSno,
    options: {
      title: 'LIVE 채널 - 에이블리 스토어',
    },
  },
  {
    name: 'OrderStatus',
    component: OrderStatusScreen,
    path: WEB_ROUTE_PATHS.OrderStatus,
    options: {
      title: '주문 상태 - 에이블리 스토어',
    },
  },
  // 주문 상세에서 파생되는 화면들 (`/orders/:orderSno/...`)
  {
    name: 'OrderCancellations',
    component: OrderCancellationsScreen,
    path: WEB_ROUTE_PATHS.OrderCancellations,
    options: {
      title: '주문 취소 - 에이블리 스토어',
    },
  },
  {
    name: 'OrderCancellationsDetail',
    component: OrderCancellationsDetailScreen,
    path: WEB_ROUTE_PATHS.OrderCancellationsDetail,
    options: {
      title: '취소 상세 - 에이블리 스토어',
    },
  },
  {
    name: 'OrderExchange',
    component: OrderExchangeScreen,
    path: WEB_ROUTE_PATHS.OrderExchange,
    options: {
      title: '교환요청 - 에이블리 스토어',
    },
  },
  {
    name: 'OrderExchangeSellers',
    component: OrderExchangeSellersScreen,
    path: WEB_ROUTE_PATHS.OrderExchangeSellers,
    options: {
      title: '교환요청 - 에이블리 스토어',
    },
  },
  {
    name: 'OrderReturn',
    component: OrderReturnScreen,
    path: WEB_ROUTE_PATHS.OrderReturn,
    options: {
      title: '반품요청 - 에이블리 스토어',
    },
  },
  {
    name: 'OrderReturnDetail',
    component: OrderReturnDetailScreen,
    path: WEB_ROUTE_PATHS.OrderReturnDetail,
    options: {
      title: '반품상세 - 에이블리 스토어',
    },
  },
  {
    name: 'OrderSalesInvoice',
    component: OrderSalesInvoiceScreen,
    path: WEB_ROUTE_PATHS.OrderSalesInvoice,
    options: {
      title: '거래명세서 - 에이블리 스토어',
    },
  },
  {
    name: 'OrderDeliveryPost',
    component: OrderDeliveryPostScreen,
    path: WEB_ROUTE_PATHS.OrderDeliveryPost,
    options: {
      title: '배송 정보 - 에이블리 스토어',
    },
  },
  {
    name: 'OrderDeliveryPostOrderItem',
    component: OrderDeliveryPostOrderItemScreen,
    path: WEB_ROUTE_PATHS.OrderDeliveryPostOrderItem,
    options: {
      title: '배송조회 - 에이블리 스토어',
    },
  },
  {
    name: 'TodayDelivery',
    component: TodayDeliveryScreen,
    path: WEB_ROUTE_PATHS.TodayDelivery,
    options: {
      title: '오늘출발 - 에이블리 스토어',
    },
  },
  // 경로 + 타이틀만 다른 웹뷰 전용 화면들(pages/WebviewScreens)
  {
    name: 'Help',
    component: HelpScreen,
    path: WEB_ROUTE_PATHS.Help,
    options: {
      title: '고객센터 - 에이블리 스토어',
    },
  },
  {
    name: 'HelpInquiry',
    component: HelpInquiryScreen,
    path: WEB_ROUTE_PATHS.HelpInquiry,
    options: {
      title: '1:1 문의 - 에이블리 스토어',
    },
  },
  {
    name: 'Coupons',
    component: CouponsScreen,
    path: WEB_ROUTE_PATHS.Coupons,
    options: {
      title: '쿠폰 - 에이블리 스토어',
    },
  },
  {
    name: 'Points',
    component: PointsScreen,
    path: WEB_ROUTE_PATHS.Points,
    options: {
      title: '포인트 - 에이블리 스토어',
    },
  },
  {
    name: 'Membership',
    component: MembershipScreen,
    path: WEB_ROUTE_PATHS.Membership,
    options: {
      title: '멤버십 - 에이블리 스토어',
    },
  },
  {
    name: 'Notifications',
    component: NotificationsScreen,
    path: WEB_ROUTE_PATHS.Notifications,
    options: {
      title: '알림 - 에이블리 스토어',
    },
  },
  {
    name: 'CommunityTalkHome',
    component: CommunityTalkHomeScreen,
    path: WEB_ROUTE_PATHS.CommunityTalkHome,
    options: {
      title: '커뮤니티 - 에이블리 스토어',
    },
  },
  {
    name: 'FunTalk',
    component: FunTalkScreen,
    path: WEB_ROUTE_PATHS.FunTalk,
    options: {
      title: '커뮤니티 - 에이블리 스토어',
    },
  },
  {
    name: 'ChatBot',
    component: ChatBotScreen,
    path: WEB_ROUTE_PATHS.ChatBot,
    options: {
      title: 'AI 채팅 - 에이블리 스토어',
    },
  },
  {
    name: 'FortuneHome',
    component: FortuneHomeScreen,
    path: WEB_ROUTE_PATHS.FortuneHome,
    options: {
      title: '운세 - 에이블리 스토어',
    },
  },
  {
    name: 'LifeFortuneDaily',
    component: LifeFortuneDailyScreen,
    path: WEB_ROUTE_PATHS.LifeFortuneDaily,
    options: {
      title: '운세 - 에이블리 스토어',
    },
  },
  {
    name: 'ContentStyleHome',
    component: ContentStyleHomeScreen,
    path: WEB_ROUTE_PATHS.ContentStyleHome,
    options: {
      title: '스타일 - 에이블리 스토어',
    },
  },
  {
    name: 'NovelHome',
    component: NovelHomeScreen,
    path: WEB_ROUTE_PATHS.NovelHome,
    options: {
      title: '웹소설 - 에이블리 스토어',
    },
  },
  {
    name: 'Style',
    component: StyleScreen,
    path: WEB_ROUTE_PATHS.Style,
    options: {
      title: '매거진 - 에이블리 스토어',
    },
  },
  {
    name: 'Benefits',
    component: BenefitsScreen,
    path: WEB_ROUTE_PATHS.Benefits,
    options: {
      title: '혜택 - 에이블리 스토어',
    },
  },
  ...devOnlyScreens,
];
